import React, { useState, useEffect, useRef } from 'react';
import { Share2, Download, Flame, MessageCircle, Clock, Zap } from 'lucide-react';
import { apiClient } from '../../api/client';

const ConfidenceReplayCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    apiClient.request('/api/enhancements/confidence-replay')
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleShare = async () => {
    if (!data?.card) return;
    const c = data.card;
    const text = `My HiBFF Confidence Replay\nScore: ${c.overall_score}/100 | Streak: ${c.streak} days | Stage: ${c.evolution_stage}\nPracticed with ${c.ava_name}: "${c.session_topic}"\n\nBuild confidence at hibff.com`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'My Confidence Replay', text });
      } else {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div data-testid="confidence-replay-card" className="bg-white rounded-2xl border border-gray-100 p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-40 mb-4" />
        <div className="h-40 bg-gray-100 rounded" />
      </div>
    );
  }

  if (!data?.has_replay) {
    return (
      <div data-testid="confidence-replay-card" className="bg-white rounded-2xl border border-gray-100 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-2">Confidence Replay</h3>
        <p className="text-sm text-gray-500">{data?.message || 'Complete your first conversation to unlock your Confidence Replay!'}</p>
      </div>
    );
  }

  const c = data.card;
  const scoreColor = c.overall_score >= 80 ? '#10b981' : c.overall_score >= 60 ? '#f59e0b' : c.overall_score >= 40 ? '#f97316' : '#ef4444';

  return (
    <div data-testid="confidence-replay-card" className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="p-6 pb-3">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Confidence Replay</h3>
          <button
            data-testid="share-replay-btn"
            onClick={handleShare}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors"
          >
            <Share2 className="w-3.5 h-3.5" />
            {copied ? 'Copied!' : 'Share'}
          </button>
        </div>
      </div>

      {/* The shareable card */}
      <div ref={cardRef} className="mx-6 mb-6 rounded-2xl overflow-hidden" style={{ background: `linear-gradient(135deg, #1e1b4b, #312e81, #4c1d95)` }}>
        <div className="p-5">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs text-violet-300 font-medium uppercase tracking-wider">Best Session</p>
              <p className="text-white font-bold text-lg mt-0.5">{c.session_topic || 'Practice Session'}</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-black" style={{ color: scoreColor }}>{c.overall_score}</div>
              <p className="text-xs text-violet-300">score</p>
            </div>
          </div>

          {/* Score breakdown */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            {[
              { label: 'Empathy', value: c.empathy_score, icon: '💛' },
              { label: 'Clarity', value: c.clarity_score, icon: '💎' },
              { label: 'Confidence', value: c.confidence_score, icon: '🔥' },
            ].map(s => (
              <div key={s.label} className="bg-white/10 rounded-xl p-2.5 text-center backdrop-blur-sm">
                <p className="text-lg font-bold text-white">{s.value || '—'}</p>
                <p className="text-xs text-violet-300">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Stats row */}
          <div className="flex items-center justify-between text-violet-300">
            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1">
                <MessageCircle className="w-3.5 h-3.5" /> {c.message_count} msgs
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> {c.duration_minutes}m
              </span>
              <span className="flex items-center gap-1">
                <Flame className="w-3.5 h-3.5" /> {c.streak} day streak
              </span>
            </div>
            <div className="flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ background: `${c.evolution_color}30`, color: c.evolution_color }}>
              <Zap className="w-3 h-3" /> {c.evolution_stage}
            </div>
          </div>

          {/* Footer */}
          <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between">
            <p className="text-sm text-white font-medium">{c.user_name}</p>
            <p className="text-xs text-violet-400">hibff.com</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfidenceReplayCard;
